import { Component, inject, ChangeDetectorRef } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatDialogRef, MatDialogClose, MatDialogContent, MatDialogActions, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatIconButton } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatInputModule } from "@angular/material/input";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { Bruger } from "@e-forsyning/common/bruger";
import { UmbracoIndstillinger } from "@e-forsyning/common/umbraco-indstillinger";
import { ToastService } from "@dffedb/ui";
import { SvgIconRegistry } from "@dffedb/util/src";
import { mdiClose, mdiChevronDown, mdiCamera } from "@mdi/js";
import type { DeviceInfo, FeedbackData } from "./feedback-dialog.interface";
import { ServiceProFeedbackMailService } from "./service-pro-feedback-mail.service";

@Component({
    selector: "feedback-dialog",
    templateUrl: "./feedback-dialog.component.html",
    styleUrl: "./feedback-dialog.component.scss",
    imports: [
        CommonModule,
        MatIconButton,
        MatDialogClose,
        MatIcon,
        MatDialogContent,
        MatDialogActions,
        MatButton,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        MatCheckboxModule
    ]
})
export class FeedbackDialogComponent {
    private readonly dialogRef = inject(MatDialogRef<FeedbackDialogComponent>);
    private readonly formbuilder = inject(FormBuilder);
    private readonly dialogData = inject(MAT_DIALOG_DATA, { optional: true });
    private readonly svgIconRegistry = inject(SvgIconRegistry);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly router = inject(Router);
    private readonly feedbackMailService = inject(ServiceProFeedbackMailService);
    private readonly toastService = inject(ToastService);

    public aktuelBruger: Bruger | null = null;
    public indstillinger: UmbracoIndstillinger | null = null;
    public isDataListExpanded = false;
    public screenshotBlob: Blob | null = null;
    public isCapturingScreenshot = false;
    public isSending = false;
    public submitError: string | null = null;
    /** True når brugeren ikke har en registreret e-mail — så skal feltet udfyldes i dialogen. */
    public requiresEmailInput = false;

    public feedbackForm: FormGroup;
    public feedbackTypes = [
        { value: "feedback", label: "Feedback" },
        { value: "fejl", label: "Fejl" }
    ];

    constructor() {
        this.svgIconRegistry.registerIcon(mdiClose, "close");
        this.svgIconRegistry.registerIcon(mdiChevronDown, "chevron-down");
        this.svgIconRegistry.registerIcon(mdiCamera, "camera");

        if (this.dialogData) {
            this.aktuelBruger = this.dialogData.aktuelBruger || null;
            this.indstillinger = this.dialogData.indstillinger || null;
        }

        this.requiresEmailInput = !this.hasRegisteredEmail(this.aktuelBruger?.email);

        this.feedbackForm = this.formbuilder.group({
            type: ["", Validators.required],
            area: [this.getCurrentRouteName(), Validators.required],
            email: ["", this.requiresEmailInput ? [Validators.required, Validators.email] : []],
            message: ["", [Validators.required, Validators.minLength(10)]],
            agreeToShare: [false, Validators.requiredTrue]
        });
    }

    private hasRegisteredEmail(email: string | null | undefined): boolean {
        const value = (email || "").trim();
        return value.length > 0 && value.includes("@");
    }

    private getCurrentRouteName(): string {
        const url = this.router.url;
        const segments = url.split("/").filter((segment) => segment.length > 0);
        return segments[0] || "";
    }

    private getDeviceInfo(): DeviceInfo {
        const userAgent = navigator.userAgent;
        let deviceType = "Desktop";
        let version = "Unknown";

        if (userAgent.includes("iPhone")) {
            deviceType = "iPhone";
        } else if (userAgent.includes("iPad")) {
            deviceType = "iPad";
        } else if (userAgent.includes("Android")) {
            deviceType = "Android";
        } else if (userAgent.includes("Mobile")) {
            deviceType = "Mobile";
        }

        const versionPatterns = [/Chrome\/([\d.]+)/, /Firefox\/([\d.]+)/, /Version\/([\d.]+)/, /Edg\/([\d.]+)/, /OS ([\d_]+)/];

        for (const pattern of versionPatterns) {
            const match = userAgent.match(pattern);
            if (match) {
                version = match[1].replace(/_/g, ".");
                break;
            }
        }

        return {
            deviceType,
            version,
            userAgent
        };
    }

    public onSubmit(): void {
        this.submitError = null;
        if (!this.feedbackForm.valid || !this.feedbackForm.get("agreeToShare")?.value || this.isSending) {
            return;
        }

        const formData = this.feedbackForm.value;
        const fromEmail = this.requiresEmailInput ? (formData.email || "").trim() : (this.aktuelBruger?.email || "").trim();
        if (!this.hasRegisteredEmail(fromEmail)) {
            this.submitError = "Indtast en gyldig e-mailadresse.";
            return;
        }

        const deviceInfo = this.getDeviceInfo();

        const feedbackData: FeedbackData = {
            type: formData.type,
            area: formData.area,
            message: formData.message,
            deviceInfo,
            bruger: this.aktuelBruger?.navn || null,
            vaerk: this.indstillinger?.firmaNavn || null,
            timestamp: new Date().toISOString()
        };

        const emailBody = this.formatEmailBody(feedbackData);
        const subject = `${formData.type === "feedback" ? "Feedback" : "Fejlrapport"} fra ServicePro`;
        const sideId = this.indstillinger?.aktuelEforsyningNodeId != null ? String(this.indstillinger.aktuelEforsyningNodeId) : undefined;

        this.isSending = true;
        this.feedbackMailService
            .sendWithOptionalScreenshot(fromEmail, subject, emailBody, this.screenshotBlob, sideId, {
                user: feedbackData.bruger,
                vaerk: feedbackData.vaerk,
                deviceType: feedbackData.deviceInfo.deviceType,
                version: feedbackData.deviceInfo.version,
                userAgent: feedbackData.deviceInfo.userAgent,
                timestamp: feedbackData.timestamp
            })
            .subscribe({
                next: () => {
                    this.isSending = false;
                    this.toastService.visInfo("Feedback er sendt");
                    this.dialogRef.close(true);
                },
                error: (err) => {
                    this.isSending = false;
                    this.submitError = this.extractErrorMessage(err);
                    this.cdr.detectChanges();
                }
            });
    }

    private formatEmailBody(feedbackData: FeedbackData): string {
        return `Type: ${feedbackData.type}
Område: ${feedbackData.area}
Besked:
${feedbackData.message}
`;
    }

    private extractErrorMessage(err: any): string {
        const serverMessage = err?.error;
        if (typeof serverMessage === "string" && serverMessage.trim()) {
            return serverMessage;
        }
        if (err?.message) {
            return err.message;
        }
        return "Kunne ikke sende feedback. Prøv igen.";
    }

    public toggleDataList(): void {
        this.isDataListExpanded = !this.isDataListExpanded;
    }

    public async captureScreenshot(): Promise<void> {
        this.isCapturingScreenshot = true;
        try {
            const html2canvas = (await import("html2canvas")).default;
            const canvas = await html2canvas(document.body, {
                allowTaint: true,
                useCORS: true,
                logging: false,
                scale: 1,
                removeContainer: true,
                ignoreElements: (element) => {
                    const tag = element.tagName.toLowerCase();
                    return tag === "script" || tag === "style";
                }
            });
            canvas.toBlob((blob) => {
                if (blob) {
                    this.screenshotBlob = blob;
                }
                this.isCapturingScreenshot = false;
                this.cdr.detectChanges();
            });
        } catch (error) {
            console.error("Failed to capture screenshot:", error);
            alert("Der skete en fejl under forsøg på at tage screenshot.");
            this.isCapturingScreenshot = false;
        }
    }

    public clearScreenshot(): void {
        this.screenshotBlob = null;
    }

    public onClose(): void {
        this.dialogRef.close();
    }
}
